import { Badge } from '@/components/ui/badge';
import { Star, GraduationCap, CheckCircle, Lightbulb } from 'lucide-react';
import { FormationIcon } from './FormationIcon';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface FormationHeroProps {
  title: string;
  description: string;
  image: string | null;
  price_ht: number;
  price_ttc: number;
  category: string;
}

export function FormationHero({
  title,
  description,
  image,
  price_ht,
  price_ttc,
  category,
}: FormationHeroProps) {
  const { t, i18n } = useTranslation('formations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  return (
    <section className="bg-gradient-to-br from-aspexit-red/8 via-white to-aspexit-orange/8 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Icon SVG */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-aspexit-red/20 to-aspexit-orange/20 p-8 flex items-center justify-center">
                <FormationIcon category={category} className="w-48 h-48" />
              </div>
            </div>

            {/* Right: Content */}
            <div className="order-1 lg:order-2">
              {/* QUALIOPI Badge */}
              <Badge className="mb-4 bg-aspexit-red hover:bg-aspexit-red-dark text-white border-0 px-4 py-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {t('hero.qualiopiBadge')}
              </Badge>

              {/* Category */}
              <div className="mb-4">
                <Badge variant="outline" className="text-aspexit-purple border-aspexit-purple">
                  {category}
                </Badge>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-6 leading-tight">
                {title}
              </h1>

              {/* Description */}
              <p className="text-lg text-aspexit-gray mb-6 leading-relaxed">
                {description}
              </p>

              {/* Rating (mock) */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <span className="text-aspexit-gray">{t('hero.rating', { rating: '4.9', count: 12 })}</span>
              </div>

              {/* Pricing */}
              <div className="mb-6">
                <div className="text-5xl font-bold text-aspexit-red mb-2">
                  {price_ttc}€ <span className="text-xl text-aspexit-gray">{t('hero.priceTtc')}</span>
                </div>
                <div className="text-aspexit-gray">
                  ({price_ht}€ {t('hero.priceHt')})
                </div>
                <div className="text-sm text-aspexit-gray mt-1">
                  {t('hero.perPerson')}
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a
                  href="https://noteforms.com/forms/inscription-a-une-formation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-white text-aspexit-black border-2 border-border rounded-lg font-semibold hover:bg-aspexit-gray-light transition-colors text-center shadow-lg hover:shadow-xl"
                >
                  {t('hero.registerCta')}
                </a>
                <a
                  href={`/${currentLocale}/contact`}
                  className="px-8 py-4 bg-white text-aspexit-black border-2 border-border rounded-lg font-semibold hover:bg-aspexit-gray-light transition-colors text-center"
                >
                  {t('hero.quoteCta')}
                </a>
              </div>

              {/* Financing Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-aspexit-gray flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <span dangerouslySetInnerHTML={{ __html: t('hero.financingInfo') }} />
                </p>
              </div>

              {/* Group Pricing Info */}
              <div className="bg-aspexit-green/10 border border-aspexit-green/30 rounded-lg p-4">
                <p className="text-sm text-aspexit-gray flex items-start gap-2">
                  <GraduationCap className="w-5 h-5 text-aspexit-green flex-shrink-0 mt-0.5" />
                  <span dangerouslySetInnerHTML={{ __html: t('hero.groupPricingInfo') }} />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
